import { Loader2 } from 'lucide-react';
import { useId, useState, type KeyboardEvent } from 'react';
import styles from '../SegmentVisualBuilder.module.css';
import { SuggestionOption } from './SuggestionOption';
import type {
  SuggestedTextInputProps,
  SuggestionOption as SuggestionOptionValue,
} from './suggested-text-input.types';

const LOADING_SUGGESTIONS_LABEL = 'Loading value suggestions';

interface SuggestionInputProps extends SuggestedTextInputProps {
  suggestions: string[];
  isLoading: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function SuggestionInput({
  value,
  onChange,
  onCommit,
  disabled,
  className,
  inputClassName = styles.textInput,
  placeholder,
  ariaLabel = 'Value',
  ariaInvalid,
  embedded = false,
  showCreateOption = false,
  suggestions,
  isLoading,
  onOpenChange,
}: SuggestionInputProps) {
  const listboxId = useId();
  const [suggestionMenuIsOpen, setSuggestionMenuIsOpen] = useState(false);
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
  const normalizedValue = value.trim();
  const matchingSuggestions = suggestions.filter(suggestion =>
    suggestion.toLowerCase().includes(normalizedValue.toLowerCase()),
  );
  const suggestionsHaveExactMatch = suggestions.some(
    suggestion => suggestion.toLowerCase() === normalizedValue.toLowerCase(),
  );
  const valueCanBeCreated =
    showCreateOption && !isLoading && !!normalizedValue && !suggestionsHaveExactMatch;
  const options: SuggestionOptionValue[] = [
    ...matchingSuggestions.map(suggestion => ({ value: suggestion, isNew: false })),
    ...(valueCanBeCreated ? [{ value: normalizedValue, isNew: true }] : []),
  ];
  const menuIsVisible = suggestionMenuIsOpen && (isLoading || options.length > 0);
  const defaultContainerClass = embedded
    ? `${styles.suggestionInput} ${styles.embeddedSuggestionInput}`
    : styles.suggestionInput;
  const containerClass = className ? `${defaultContainerClass} ${className}` : defaultContainerClass;
  const activeOptionId =
    menuIsVisible && activeOptionIndex >= 0 && activeOptionIndex < options.length
      ? `${listboxId}-option-${activeOptionIndex}`
      : undefined;

  const setOpen = (nextIsOpen: boolean) => {
    setSuggestionMenuIsOpen(nextIsOpen);
    if (!nextIsOpen) setActiveOptionIndex(-1);
    onOpenChange?.(nextIsOpen);
  };

  const selectValue = (nextValue: string) => {
    if (onCommit) onCommit(nextValue);
    else onChange(nextValue);
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && options.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActiveOptionIndex(currentIndex => (currentIndex + 1) % options.length);
      return;
    }
    if (event.key === 'ArrowUp' && options.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActiveOptionIndex(currentIndex => (currentIndex <= 0 ? options.length - 1 : currentIndex - 1));
      return;
    }
    if (event.key === 'Escape') {
      if (menuIsVisible) {
        event.preventDefault();
        event.stopPropagation();
      }
      setOpen(false);
      return;
    }
    if (event.key !== 'Enter') return;

    const activeOption = options[activeOptionIndex];
    if (activeOption) {
      event.preventDefault();
      selectValue(activeOption.value);
      return;
    }
    if (!normalizedValue || !onCommit) return;
    event.preventDefault();
    selectValue(normalizedValue);
  };

  return (
    <div
      className={containerClass}
      onBlur={event => {
        const focusStayedWithinInput =
          event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget);
        if (!focusStayedWithinInput) setOpen(false);
      }}>
      <input
        type="text"
        className={inputClassName}
        value={value}
        onChange={event => {
          onChange(event.target.value);
          setActiveOptionIndex(-1);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-expanded={menuIsVisible}
        aria-controls={menuIsVisible ? listboxId : undefined}
        aria-activedescendant={activeOptionId}
        aria-busy={isLoading}
      />
      {menuIsVisible && (
        <div className={styles.valueSuggestionDropdown}>
          {isLoading && (
            <p
              className={styles.valueSuggestionLoading}
              role="status"
              aria-label={LOADING_SUGGESTIONS_LABEL}>
              <Loader2 aria-hidden />
              {LOADING_SUGGESTIONS_LABEL}
            </p>
          )}
          <ul id={listboxId} role="listbox" aria-label={`${ariaLabel} suggestions`}>
            {options.map((option, index) => (
              <SuggestionOption
                key={`${option.isNew ? 'new' : 'existing'}-${option.value}`}
                id={`${listboxId}-option-${index}`}
                option={option}
                isActive={index === activeOptionIndex}
                isSelected={option.value === value}
                onActivate={() => setActiveOptionIndex(index)}
                onSelect={() => selectValue(option.value)}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
